import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp, query, where, getDocs, collection } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';

// 🌐 한글 감지 정규식
const koreanRegex = /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/;

// 🔤 영어 전환 함수
const convertToEnglish = (text: string): string => {
  // 한글을 영어로 변환하는 간단한 매핑
  const koreanToEnglish: { [key: string]: string } = {
    'ㅂ': 'q', 'ㅈ': 'w', 'ㄷ': 'e', 'ㄱ': 'r', 'ㅅ': 't',
    'ㅛ': 'y', 'ㅕ': 'u', 'ㅑ': 'i', 'ㅐ': 'o', 'ㅔ': 'p',
    'ㅁ': 'a', 'ㄴ': 's', 'ㅇ': 'd', 'ㄹ': 'f', 'ㅎ': 'g',
    'ㅗ': 'h', 'ㅓ': 'j', 'ㅏ': 'k', 'ㅣ': 'l',
    'ㅋ': 'z', 'ㅌ': 'x', 'ㅊ': 'c', 'ㅍ': 'v', 'ㅠ': 'b',
    'ㅜ': 'n', 'ㅡ': 'm'
  };

  return text.split('').map(char => {
    if (koreanToEnglish[char]) {
      return koreanToEnglish[char];
    }
    // 한글이지만 매핑되지 않은 경우 제거
    if (koreanRegex.test(char)) {
      return '';
    }
    return char;
  }).join('');
};

// Analytics tracking function
const trackProfilePathSet = (path: string) => {
  console.log('Custom profile path set:', path);
};

const trackCustomDomainSet = (domain: string) => {
  console.log('Custom domain set:', domain);
};

interface CustomPathData {
  path: string;
  status: 'active' | 'pending_approval' | 'rejected';
  createdAt: any;
}

interface CustomDomainData {
  domain: string;
  status: 'pending' | 'verified' | 'failed';
  createdAt: any;
  verifiedAt?: any;
}

const DomainSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { canUseCustomDomain, isPro, isExpired } = useSubscription();
  const { user } = useAuth();
  
  // URL 파라미터에서 정보 추출
  const searchParams = new URLSearchParams(location.search);
  const cardId = searchParams.get('cardId') || '';
  const initialPath = searchParams.get('initialPath') || '';
  const initialDomain = searchParams.get('initialDomain') || '';
  const isCustomDomain = searchParams.get('isCustomDomain') === 'true';
  
  const [profilePath, setProfilePath] = useState(initialPath);
  const [isValidPath, setIsValidPath] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [existingPathData, setExistingPathData] = useState<CustomPathData | null>(null);
  const [showKoreanWarning, setShowKoreanWarning] = useState(false);
  const [pathAvailabilityStatus, setPathAvailabilityStatus] = useState<'unknown' | 'available' | 'taken' | 'current' | 'invalid'>('unknown');
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  
  // Custom Domain states
  const [customDomain, setCustomDomain] = useState(initialDomain);
  const [isValidDomain, setIsValidDomain] = useState(false);
  const [customDomainLoading, setCustomDomainLoading] = useState(false);
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [existingDomainData, setExistingDomainData] = useState<CustomDomainData | null>(null);
  const [dnsGuideVisible, setDnsGuideVisible] = useState(false);
  
  const userId = user?.uid;

  // Profile path validation regex
  const pathRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  
  // Domain validation regex
  const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9])*$/;

  // Debug logging for auth state
  useEffect(() => {
    console.log("DomainSettingsPage auth user:", user);
  }, [user]);

  // 🔒 커스텀 도메인 접근 제한 확인
  useEffect(() => {
    if (isCustomDomain && !canUseCustomDomain) {
      // Pro 플랜이 아닌 경우 Paywall로 리다이렉트
      navigate('/paywall', { 
        state: { limitType: 'custom_domain' }
      });
    }
  }, [isCustomDomain, canUseCustomDomain, navigate]);

  useEffect(() => {
    if (userId) {
      if (isCustomDomain) {
        fetchExistingDomain();
      } else {
        fetchExistingPath();
      }
    }
    // Initialize validation for existing values
    if (initialPath) {
      validatePath(initialPath);
    }
    if (initialDomain) {
      validateDomain(initialDomain);
    }
  }, [userId, isCustomDomain, initialPath, initialDomain]);

  const fetchExistingPath = async () => {
    if (!userId || !cardId) return;
    setLoading(true);
    try {
      const docRef = doc(db, 'users', userId, 'cards', cardId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data?.customPath) {
          setExistingPathData({
            path: data.customPath,
            status: 'active',
            createdAt: data.updatedAt || data.createdAt
          });
          setProfilePath(data.customPath);
          validatePath(data.customPath);

          // 마이그레이션: customPaths 컬렉션에 데이터가 없다면 추가
          const customPathRef = doc(db, 'customPaths', data.customPath);
          const customPathSnap = await getDoc(customPathRef);
          
          if (!customPathSnap.exists()) {
            console.log('Migrating existing custom path to customPaths collection:', data.customPath);
            await setDoc(customPathRef, {
              userId: userId,
              cardId: cardId,
              status: 'active',
              createdAt: data.updatedAt || data.createdAt || serverTimestamp(),
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching existing profile path:', error);
      showSnackbar(t('domainSettings.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingDomain = async () => {
    if (!userId || !cardId) return;
    setCustomDomainLoading(true);
    try {
      const docRef = doc(db, 'users', userId, 'cards', cardId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data?.customDomain) {
          setExistingDomainData({
            domain: data.customDomain,
            status: data.domainStatus || 'pending',
            createdAt: data.updatedAt || data.createdAt
          });
          setCustomDomain(data.customDomain);
          validateDomain(data.customDomain);
        }
      }
    } catch (error) {
      console.error('Error fetching existing custom domain:', error);
      showSnackbar(t('domainSettings.fetchError'));
    } finally {
      setCustomDomainLoading(false);
    }
  };

  const validatePath = (input: string) => {
    const trimmed = input.trim().toLowerCase();
    const valid = pathRegex.test(trimmed) && trimmed.length >= 3 && trimmed.length <= 30;
    setIsValidPath(valid);
    return valid;
  };

  const validateDomain = (input: string) => {
    const trimmed = input.trim().toLowerCase();
    const valid = domainRegex.test(trimmed) && trimmed.length >= 4 && trimmed.length <= 253;
    setIsValidDomain(valid);
    return valid;
  };

  const handlePathChange = (text: string) => {
    // 기존 타이머 클리어
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // 🚨 한글 입력 감지 및 처리
    if (koreanRegex.test(text)) {
      setShowKoreanWarning(true);
      // 한글을 영어로 자동 변환 시도
      const convertedText = convertToEnglish(text);
      const lowercasedText = convertedText.toLowerCase();
      setProfilePath(lowercasedText);
      validatePath(lowercasedText);
      
      // 경고 메시지 표시
      showSnackbar(t('domainSettings.koreanInputDetected'));
      
      // 3초 후 경고 숨기기
      setTimeout(() => setShowKoreanWarning(false), 3000);
      
      // 자동 중복 검사 설정
      if (lowercasedText.trim() && validatePath(lowercasedText)) {
        const timer = setTimeout(() => {
          checkPathAvailability(lowercasedText);
        }, 500);
        setDebounceTimer(timer);
      } else {
        setPathAvailabilityStatus('unknown');
      }
      return;
    }
    
    // 영어, 숫자, 하이픈만 허용
    const filteredText = text.replace(/[^a-zA-Z0-9-]/g, '');
    const lowercasedText = filteredText.toLowerCase();
    setProfilePath(lowercasedText);
    validatePath(lowercasedText);
    setShowKoreanWarning(false);
    
    // 실시간 중복 검사 설정
    if (lowercasedText.trim() && validatePath(lowercasedText)) {
      const timer = setTimeout(() => {
        checkPathAvailability(lowercasedText);
      }, 500);
      setDebounceTimer(timer);
    } else {
      setPathAvailabilityStatus('unknown');
    }
  };

  const handleDomainChange = (text: string) => {
    // 🚨 한글 입력 감지 및 처리
    if (koreanRegex.test(text)) {
      setShowKoreanWarning(true);
      showSnackbar(t('domainSettings.koreanInputDetected'));
      setTimeout(() => setShowKoreanWarning(false), 3000);
      return;
    }
    
    // 도메인에서 프로토콜과 www 제거 후 허용된 문자만 유지
    const cleanedText = text.replace(/^https?:\/\//, '').replace(/^www\./, '');
    const filteredText = cleanedText.replace(/[^a-zA-Z0-9.-]/g, '');
    const lowercasedText = filteredText.toLowerCase();
    setCustomDomain(lowercasedText);
    validateDomain(lowercasedText);
    setShowKoreanWarning(false);
  };

  const checkPathAvailability = async (pathToCheck?: string) => {
    const pathValue = pathToCheck || profilePath;
    
    if (!validatePath(pathValue)) {
      setPathAvailabilityStatus('invalid');
      showSnackbar(t('domainSettings.invalidPathFormat'));
      return;
    }
    if (existingPathData && existingPathData.path === pathValue.trim().toLowerCase()) {
      setPathAvailabilityStatus('current');
      showSnackbar(t('domainSettings.pathIsYourCurrent'));
      return;
    }

    setCheckingAvailability(true);
    setPathAvailabilityStatus('unknown');
    
    try {
      const pathToCheckLower = pathValue.trim().toLowerCase();
      
      // customLinks 컬렉션에서 먼저 확인 (웹 서버용)
      const customLinksQuery = query(collection(db, 'customLinks'), where('customPath', '==', pathToCheckLower));
      const customLinksSnapshot = await getDocs(customLinksQuery);
      
      if (!customLinksSnapshot.empty) {
        const existingDoc = customLinksSnapshot.docs[0];
        const existingData = existingDoc.data();
        
        if (existingData.userId !== userId || existingData.cardId !== cardId) {
          setPathAvailabilityStatus('taken');
          if (!pathToCheck) { // 수동 확인일 때만 스낵바 표시
            showSnackbar(t('domainSettings.pathTaken'));
          }
          return;
        }
      }
      
      // customPaths 컬렉션에서도 확인
      const pathDoc = await getDoc(doc(collection(db, 'customPaths'), pathToCheckLower));
      if (pathDoc.exists()) {
        const pathData = pathDoc.data();
        if (pathData && (pathData.userId !== userId || pathData.cardId !== cardId)) {
          setPathAvailabilityStatus('taken');
          if (!pathToCheck) { // 수동 확인일 때만 스낵바 표시
            showSnackbar(t('domainSettings.pathTaken'));
          }
          return;
        }
      }

      // 모든 검사를 통과하면 사용 가능
      setPathAvailabilityStatus('available');
      if (!pathToCheck) { // 수동 확인일 때만 스낵바 표시
        showSnackbar(t('domainSettings.pathAvailable'));
      }
      
    } catch (error) {
      console.error('Error checking path availability:', error);
      setPathAvailabilityStatus('unknown');
      showSnackbar(t('domainSettings.checkError'));
    } finally {
      setCheckingAvailability(false);
    }
  };

  const saveProfilePath = async () => {
    if (!validatePath(profilePath)) {
      showSnackbar(t('domainSettings.invalidPathFormat'));
      return;
    }

    // 실시간 상태 확인 기반으로 저장 허용/차단
    if (pathAvailabilityStatus === 'taken') {
      showSnackbar(t('domainSettings.pathTaken'));
      return;
    }
    
    if (pathAvailabilityStatus === 'unknown') {
      showSnackbar(t('domainSettings.pathChecking', '경로 확인 중입니다. 잠시 후 다시 시도해주세요.'));
      return;
    }

    setLoading(true);
    try {
      const pathToSave = profilePath.trim().toLowerCase();
      
      // 1. 카드 문서에 customPath 저장
      if (!userId || !cardId) {
        throw new Error('User ID or Card ID is missing');
      }
      
      await setDoc(doc(db, 'users', userId, 'cards', cardId), {
        customPath: pathToSave,
        shareUrl: `https://scanvcf.com/${pathToSave}`,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      // 2. customLinks 컬렉션에 저장 (웹서버용 - 가장 중요)
      await setDoc(doc(db, 'customLinks', pathToSave), {
        customPath: pathToSave,
        userId: userId,
        cardId: cardId,
        status: 'active',
        shareUrl: `https://scanvcf.com/${pathToSave}`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // 3. customPaths 컬렉션에도 저장 (앱 내부용)
      await setDoc(doc(db, 'customPaths', pathToSave), {
        path: pathToSave,
        userId: userId,
        cardId: cardId,
        status: 'active',
        createdAt: serverTimestamp(),
      });

      console.log('✅ Path saved successfully to all collections:', pathToSave);
      showSnackbar(t('domainSettings.saveSuccess'));
      trackProfilePathSet(pathToSave);
      
      // 성공 후 기존 데이터 업데이트
      setExistingPathData({
        path: pathToSave,
        status: 'active',
        createdAt: serverTimestamp(),
      });
      
      // 성공 시 이전 화면으로 돌아가기
      navigate(-1);
    } catch (error) {
      console.error('Error saving profile path:', error);
      showSnackbar(t('domainSettings.saveError'));
    } finally {
      setLoading(false);
    }
  };

  const saveCustomDomain = async () => {
    if (!userId || !customDomain) return;
    
    if (!validateDomain(customDomain)) {
      showSnackbar(t('domainSettings.domainValidation.invalidFormat'));
      return;
    }
    
    setCustomDomainLoading(true);
    try {
      const domainToSave = customDomain.trim().toLowerCase();
      
      // 카드 문서에 customDomain 저장
      await setDoc(doc(db, 'users', userId, 'cards', cardId), {
        customDomain: domainToSave,
        domainStatus: 'pending',
        updatedAt: serverTimestamp(),
      }, { merge: true });

      showSnackbar(t('domainSettings.domainSaved'));
      trackCustomDomainSet(domainToSave);
      
      // 성공 시 이전 화면으로 돌아가기
      navigate(-1);
    } catch (error) {
      console.error('Error saving custom domain:', error);
      showSnackbar(t('domainSettings.saveError'));
    } finally {
      setCustomDomainLoading(false);
    }
  };

  const verifyCustomDomain = async () => {
    if (!userId || !customDomain) return;
    
    setVerifyingDomain(true);
    try {
      // Update status to pending for manual verification by admin
      const domainData: CustomDomainData = {
        domain: customDomain.trim().toLowerCase(),
        status: 'pending',
        createdAt: existingDomainData?.createdAt || serverTimestamp(),
      };

      await setDoc(doc(db, 'users', userId, 'customDomain', 'info'), domainData);

      setExistingDomainData(domainData);
      showSnackbar('Domain submitted for manual verification. Our team will verify within 24-48 hours.');

    } catch (error) {
      console.error('Error verifying custom domain:', error);
      showSnackbar(t('domainSettings.domainValidation.verificationFailed'));
    } finally {
      setVerifyingDomain(false);
    }
  };

  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarVisible(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return '#4caf50';
      case 'pending': return '#ff9800';
      case 'failed': return '#f44336';
      default: return '#757575';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'verified': return t('domainSettings.statusVerified');
      case 'pending': return t('domainSettings.statusPending');
      case 'failed': return t('domainSettings.statusFailed');
      default: return '';
    }
  };

  const fullProfileUrl = existingPathData && existingPathData.path ? `scanvcf.com/${existingPathData.path}` : '';

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isCustomDomain ? t('domainSettings.customDomainTitle') : t('domainSettings.title')}
          </h1>
          <p className="text-gray-600">
            {isCustomDomain ? t('domainSettings.customDomainDescription') : t('domainSettings.descriptionPath')}
          </p>
        </div>

        {/* Profile Path Section - Only show when isCustomDomain is false */}
        {!isCustomDomain && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="mb-6">
              <label htmlFor="profilePath" className="block text-sm font-medium text-gray-700 mb-2">
                {t('domainSettings.customPathLabel')}
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                  scanvcf.com/
                </span>
                <input
                  type="text"
                  id="profilePath"
                  value={profilePath}
                  onChange={(e) => handlePathChange(e.target.value)}
                  placeholder={t('domainSettings.pathPlaceholder')}
                  className={`flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md focus:ring-blue-500 focus:border-blue-500 text-sm border-gray-300 ${
                    profilePath.length > 0 && (!isValidPath || showKoreanWarning) ? 'border-red-300' : ''
                  }`}
                  maxLength={30}
                />
              </div>
              {showKoreanWarning && (
                <p className="mt-1 text-sm text-red-600">
                  {t('domainSettings.koreanInputDetected')}
                </p>
              )}
              {profilePath.length > 0 && !isValidPath && (
                <p className="mt-1 text-sm text-red-600">
                  {t('domainSettings.invalidPathHelperText')}
                </p>
              )}
              {profilePath.length === 0 && (
                <p className="mt-1 text-sm text-gray-500">
                  {t('domainSettings.englishOnlyHelperText')}
                </p>
              )}
            </div>

            {existingPathData && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  <span className="text-blue-700 font-medium">
                    {t('domainSettings.currentPath')}: {fullProfileUrl}
                  </span>
                </div>
              </div>
            )}

            {/* 실시간 상태 표시 */}
            {profilePath.trim() && isValidPath && (
              <div className="mb-6 flex items-center">
                {checkingAvailability && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                )}
                <span className={`text-sm ${
                  pathAvailabilityStatus === 'available' ? 'text-green-600' : 
                  pathAvailabilityStatus === 'taken' ? 'text-red-600' : 
                  pathAvailabilityStatus === 'current' ? 'text-blue-600' : 'text-gray-600'
                }`}>
                  {pathAvailabilityStatus === 'available' && t('domainSettings.pathAvailable')}
                  {pathAvailabilityStatus === 'taken' && t('domainSettings.pathTaken')}
                  {pathAvailabilityStatus === 'current' && t('domainSettings.pathIsYourCurrent')}
                  {pathAvailabilityStatus === 'unknown' && checkingAvailability && t('domainSettings.checking', '확인 중...')}
                </span>
              </div>
            )}

            <button
              onClick={saveProfilePath}
              disabled={
                !isValidPath || 
                loading || 
                checkingAvailability || 
                pathAvailabilityStatus === 'taken' ||
                pathAvailabilityStatus === 'unknown'
              }
              className={`w-full py-2 px-4 rounded-md text-white font-medium ${
                !isValidPath || loading || checkingAvailability || pathAvailabilityStatus === 'taken' || pathAvailabilityStatus === 'unknown'
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('domainSettings.saving')}
                </div>
              ) : (
                t('domainSettings.savePath')
              )}
            </button>
          </div>
        )}

        {/* Custom Domain Section - Only show when isCustomDomain is true */}
        {isCustomDomain && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="mb-6">
              <label htmlFor="customDomain" className="block text-sm font-medium text-gray-700 mb-2">
                {t('domainSettings.customDomainLabel')}
              </label>
              <input
                type="text"
                id="customDomain"
                value={customDomain}
                onChange={(e) => handleDomainChange(e.target.value)}
                placeholder={t('domainSettings.customDomainPlaceholder')}
                className={`block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm ${
                  customDomain.length > 0 && (!isValidDomain || showKoreanWarning) ? 'border-red-300' : ''
                }`}
              />
              {showKoreanWarning && (
                <p className="mt-1 text-sm text-red-600">
                  {t('domainSettings.koreanInputDetected')}
                </p>
              )}
              {customDomain.length > 0 && !isValidDomain && (
                <p className="mt-1 text-sm text-red-600">
                  {t('domainSettings.domainValidation.invalidFormat')}
                </p>
              )}
              {customDomain.length === 0 && (
                <p className="mt-1 text-sm text-gray-500">
                  {t('domainSettings.englishOnlyHelperText')}
                </p>
              )}
            </div>

            {existingDomainData && (
              <div className="mb-6">
                <div className="flex items-center mb-2">
                  <span className="text-sm font-medium text-gray-700 mr-2">
                    {t('domainSettings.domainStatus')}:
                  </span>
                  <span 
                    className="px-2 py-1 text-xs font-medium rounded-full"
                    style={{ 
                      backgroundColor: getStatusColor(existingDomainData.status) + '20',
                      color: getStatusColor(existingDomainData.status)
                    }}
                  >
                    {getStatusText(existingDomainData.status)}
                  </span>
                </div>
                {existingDomainData.domain && (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                      </svg>
                      <span className="text-blue-700 font-medium">
                        {existingDomainData.domain}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex space-x-4">
              <button
                onClick={() => setDnsGuideVisible(true)}
                className="flex-1 py-2 px-4 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50 font-medium"
              >
                {t('domainSettings.dnsGuideButton')}
              </button>
              
              <button
                onClick={saveCustomDomain}
                disabled={!isValidDomain || customDomainLoading}
                className={`flex-1 py-2 px-4 rounded-md text-white font-medium ${
                  !isValidDomain || customDomainLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {customDomainLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('domainSettings.saving')}
                  </div>
                ) : (
                  t('domainSettings.setupCustomDomain')
                )}
              </button>
            </div>

            {/* 도메인 검증 버튼 (기존 도메인이 있는 경우) */}
            {existingDomainData && existingDomainData.status === 'pending' && (
              <div className="mt-4">
                <button
                  onClick={verifyCustomDomain}
                  disabled={verifyingDomain}
                  className={`w-full py-2 px-4 rounded-md text-white font-medium ${
                    verifyingDomain
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {verifyingDomain ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {t('domainSettings.domainValidation.verifying', '검증 중...')}
                    </div>
                  ) : (
                    t('domainSettings.domainValidation.submitForVerification', '수동 검증 요청')
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Guide Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {!isCustomDomain ? t('domainSettings.setupGuideTitlePath') : t('domainSettings.dnsGuide.title')}
          </h2>
          <p className="text-gray-600 mb-6">
            {!isCustomDomain ? t('domainSettings.guideDescriptionPath') : t('domainSettings.dnsGuide.subtitle')}
          </p>
          
          {!isCustomDomain ? (
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4">
                  1
                </div>
                <div>
                  <p className="text-gray-900">{t('domainSettings.step1Path')}</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4">
                  2
                </div>
                <div>
                  <p className="text-gray-900">
                    {t('domainSettings.step2Path', { path: fullProfileUrl || 'scanvcf.com/[your-path]' })}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4">
                  1
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">{t('domainSettings.dnsGuide.step1Title')}</h3>
                  <p className="text-gray-600">{t('domainSettings.dnsGuide.step1Description')}</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4">
                  2
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">{t('domainSettings.dnsGuide.step2Title')}</h3>
                  <p className="text-gray-600">{t('domainSettings.dnsGuide.step2Description')}</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm mr-4">
                  3
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">{t('domainSettings.dnsGuide.step3Title')}</h3>
                  <p className="text-gray-600">{t('domainSettings.dnsGuide.step3Description')}</p>
                </div>
              </div>
              
              <div className="flex items-start mt-6 p-4 bg-yellow-50 rounded-lg">
                <svg className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-yellow-800 text-sm">{t('domainSettings.dnsGuide.dnsNote')}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Snackbar */}
      {snackbarVisible && (
        <div className="fixed bottom-4 right-4 bg-gray-900 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          {snackbarMessage}
        </div>
      )}

      {/* DNS Guide Modal */}
      {dnsGuideVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">{t('domainSettings.dnsGuide.title')}</h3>
              <button
                onClick={() => setDnsGuideVisible(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">{t('domainSettings.dnsGuide.step1Title')}</h4>
                <p className="text-gray-600">{t('domainSettings.dnsGuide.step1Description')}</p>
              </div>
              <div>
                <h4 className="font-medium mb-2">{t('domainSettings.dnsGuide.step2Title')}</h4>
                <p className="text-gray-600">{t('domainSettings.dnsGuide.step2Description')}</p>
              </div>
              <div>
                <h4 className="font-medium mb-2">{t('domainSettings.dnsGuide.step3Title')}</h4>
                <p className="text-gray-600">{t('domainSettings.dnsGuide.step3Description')}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainSettingsPage;
